import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  CircularProgress,
  TextField,
  Divider,
  Chip,
} from '@mui/material';
import { paymentService } from '../../services/payment.service';
import { toastService } from '../../services/toast.service';
import { useAppSelector } from '../../store';
import { selectUser } from '../../store/auth/authSlice';

interface DevSubscriptionToolsProps {
  domain?: 'client' | 'expert';
}

export const DevSubscriptionTools: React.FC<DevSubscriptionToolsProps> = ({ domain = 'client' }) => {
  const user = useAppSelector(selectUser);
  const [processing, setProcessing] = useState(false);
  const [targetUserId, setTargetUserId] = useState<string>('');

  useEffect(() => {
    if (user) {
      setTargetUserId(String(user.id));
    }
  }, [user]);

  const hasAccess = user?.roles?.some(role => 
    ['developer', 'superadmin'].includes(role.toLowerCase())
  );

  if (!hasAccess) {
    return null;
  }

  // Updated handler to support new params (even if backend ignores them for now)
  const handleSetStatus = async (
    status: 'active' | 'cancelled' | 'expired' | 'none', 
    days: number,
    isTrial: boolean = false,
    billingCycle: 'month' | 'year' = 'month'
  ) => {
    const userIdNum = parseInt(targetUserId, 10);

    if (!targetUserId || isNaN(userIdNum)) {
      toastService.error('Будь ласка, введіть коректний ID користувача');
      return;
    }

    console.log('DevTools: Setting status:', { status, days, isTrial, billingCycle, userId: userIdNum });
    
    setProcessing(true);
    try {
      const setStatus = domain === 'expert' ? paymentService.expertDevSetStatus : paymentService.devSetStatus;
      await setStatus(userIdNum, status, days, isTrial, billingCycle);

      let message = `Статус: ${status}`;
      if (isTrial) message += ' (TRIAL)';
      if (billingCycle === 'year') message += ' (YEAR)';
      
      toastService.success(message);
    } catch (error) {
      console.error('Failed to set status', error);
      toastService.error('Не вдалося змінити статус');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3, border: '1px dashed #ff9800', backgroundColor: '#fff3e0' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" color="warning.dark" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          🛠️ Dev Tools: Subscription State Manager
        </Typography>
        <Chip label="Developer Mode" color="warning" size="small" variant="outlined" />
      </Box>
      
      <Typography variant="body2" paragraph color="text.secondary">
        Емуляція різних станів підписки для тестування UI та доступів.
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <TextField
          label="Target User ID"
          value={targetUserId}
          onChange={(e) => setTargetUserId(e.target.value)}
          type="number"
          size="small"
          fullWidth
          helperText="ID користувача для маніпуляцій (за замовчуванням - ваш)"
        />
      </Box>

      <Grid container spacing={2}>
        {/* Standard Scenarios */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>Standard Subscription</Typography>
          <Grid container spacing={1}>
            <Grid item>
              <Button 
                variant="contained" 
                color="success" 
                size="small"
                onClick={() => handleSetStatus('active', 30, false, 'month')}
                disabled={processing}
              >
                Active (Month)
              </Button>
            </Grid>
            <Grid item>
              <Button 
                variant="contained" 
                color="success" 
                size="small"
                onClick={() => handleSetStatus('active', 360, false, 'year')}
                disabled={processing}
              >
                Active (Year)
              </Button>
            </Grid>
            <Grid item>
              <Button 
                variant="contained" 
                color="warning" 
                size="small"
                onClick={() => handleSetStatus('cancelled', 15, false, 'month')}
                disabled={processing}
              >
                Cancelled (Active)
              </Button>
            </Grid>
            <Grid item>
              <Button 
                variant="contained" 
                color="error" 
                size="small"
                onClick={() => handleSetStatus('expired', -1, false, 'month')}
                disabled={processing}
              >
                Expired (Paywall)
              </Button>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}><Divider /></Grid>

        {/* Trial Scenarios */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>Trial Scenarios (1 UAH)</Typography>
          <Grid container spacing={1}>
            <Grid item>
              <Button 
                variant="contained" 
                sx={{ bgcolor: 'info.main', '&:hover': { bgcolor: 'info.dark' } }}
                size="small"
                onClick={() => handleSetStatus('active', 29, true, 'month')}
                disabled={processing}
              >
                Trial Active
              </Button>
            </Grid>
            <Grid item>
              <Button 
                variant="contained" 
                sx={{ bgcolor: 'error.dark', '&:hover': { bgcolor: 'error.main' } }}
                size="small"
                onClick={() => handleSetStatus('expired', -1, true, 'month')}
                disabled={processing}
              >
                Trial Expired
              </Button>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}><Divider /></Grid>

        {/* Reset */}
        <Grid item xs={12}>
          <Button 
            variant="outlined" 
            color="inherit" 
            fullWidth
            onClick={() => handleSetStatus('none', 0)}
            disabled={processing}
          >
            RESET TO NEW USER (Clean Slate)
          </Button>
        </Grid>
      </Grid>
      
      {processing && (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress size={24} />
        </Box>
      )}
    </Paper>
  );
};
